import { Button } from "../../ui/components/Button";
import { Heading } from "../../ui/components/Heading";
import { Text } from "../../ui/components/Text";
import { Select, SelectItem } from "../../ui/components/Select";
import { Switch } from "../../ui/components/Switch";
import { CloudUpload, Eye, EyeOff, FileImage } from "lucide-react";
import { useCallback, useState } from "react";
import { FileTrigger } from "react-aria-components";
import type { UploadedImage, ExportFormat } from "../types";
import { luminaService, type LuminaServiceConfig } from "../services/luminaService";

interface UploadAreaProps {
  onImageUpload: (image: UploadedImage) => void;
}

export function UploadArea({ onImageUpload }: UploadAreaProps) {
  const [isDragOver, setIsDragOver] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showLicenseKey, setShowLicenseKey] = useState(false);
  const [settings, setSettings] = useState<LuminaServiceConfig>({
    licenseKey: localStorage.getItem("lumina_license_key") ?? "",
    previewQuality: 85,
    previewSize: 2048,
    enableLogging: false,
    defaultPreviewFormat: "webp",
  });

  const applySettings = useCallback(async () => {
    try {
      await luminaService.updateConfig(settings);
    } catch (error) {
      console.warn("Failed to update config:", error);
    }
  }, [settings]);

  const handleLicenseKeyChange = useCallback((key: string) => {
    localStorage.setItem("lumina_license_key", key);
    setSettings((prev) => ({ ...prev, licenseKey: key }));
  }, []);

  const handleImageLoad = useCallback(
    async (file: File) => {
      if (!settings.licenseKey?.trim()) {
        setError("Enter your Lumina license key above to load an image.");
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        // Apply current settings before loading
        await applySettings();

        // Use Lumina service to load and process the image
        const processedImage = await luminaService.loadImage(file);

        const uploadedImage: UploadedImage = {
          file,
          url: processedImage.previewUrl,
          width: processedImage.metadata.width,
          height: processedImage.metadata.height,
        };

        onImageUpload(uploadedImage);
      } catch (err) {
        console.error("Failed to load image:", err);
        const errorMessage = err instanceof Error ? err.message : "Failed to load image";
        setError(errorMessage);
      } finally {
        setIsLoading(false);
      }
    },
    [onImageUpload, applySettings, settings.licenseKey]
  );

  const handleFileSelect = useCallback(
    (fileList: FileList | null) => {
      if (!fileList || fileList.length === 0) {
        return;
      }

      const file = fileList[0];
      if (!file.type.startsWith("image/")) {
        return;
      }

      handleImageLoad(file);
    },
    [handleImageLoad]
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragOver(false);

      const files = Array.from(e.dataTransfer.files).filter((file) =>
        file.type.startsWith("image/")
      );

      if (files.length > 0) {
        handleImageLoad(files[0]); // Only take the first image for editor
      }
    },
    [handleImageLoad]
  );

  const handleDragOver = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (!isLoading) {
        setIsDragOver(true);
      }
    },
    [isLoading]
  );

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  }, []);

  return (
    <div className="w-full max-w-2xl space-y-6 px-4 md:px-0">
      {/* Settings Panel */}
      <div className="space-y-3">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <Select
            label="Preview Quality"
            selectedKey={settings.previewQuality?.toString()}
            onSelectionChange={(key) =>
              setSettings((prev) => ({ ...prev, previewQuality: parseInt(key as string) }))
            }
          >
            <SelectItem id="60">Low (60%)</SelectItem>
            <SelectItem id="75">Medium (75%)</SelectItem>
            <SelectItem id="85">High (85%)</SelectItem>
            <SelectItem id="95">Very High (95%)</SelectItem>
            <SelectItem id="100">Maximum (100%)</SelectItem>
          </Select>
          <Select
            label="Preview Size"
            selectedKey={settings.previewSize?.toString()}
            onSelectionChange={(key) =>
              setSettings((prev) => ({ ...prev, previewSize: parseInt(key as string) }))
            }
          >
            <SelectItem id="512">512px</SelectItem>
            <SelectItem id="768">768px</SelectItem>
            <SelectItem id="1024">1024px</SelectItem>
            <SelectItem id="1536">1536px</SelectItem>
            <SelectItem id="2048">2048px</SelectItem>
          </Select>
          <Select
            label="Preview Format"
            selectedKey={settings.defaultPreviewFormat || "webp"}
            onSelectionChange={(key) =>
              setSettings((prev) => ({ ...prev, defaultPreviewFormat: key as ExportFormat }))
            }
          >
            <SelectItem id="jpeg">JPEG</SelectItem>
            <SelectItem id="png">PNG</SelectItem>
            <SelectItem id="webp">WebP</SelectItem>
          </Select>
          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium leading-none">License Key</label>
            <div className="relative flex items-center">
              <input
                type={showLicenseKey ? "text" : "password"}
                value={settings.licenseKey ?? ""}
                onChange={(e) => handleLicenseKeyChange(e.target.value)}
                placeholder="Enter license key"
                className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm pr-9 text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary"
              />
              <button
                type="button"
                onClick={() => setShowLicenseKey((v) => !v)}
                className="absolute right-2 text-muted-foreground hover:text-foreground"
                tabIndex={-1}
              >
                {showLicenseKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>
        </div>
        <Switch
          isSelected={settings.enableLogging || false}
          onChange={(isSelected) => setSettings((prev) => ({ ...prev, enableLogging: isSelected }))}
        >
          Enable Debug Logging
        </Switch>
      </div>

      {/* Upload Area */}
      <div
        className={`group flex flex-col items-center justify-center gap-6 rounded-2xl border-2 border-dashed bg-background p-16 text-sm transition-all duration-200 ${
          isLoading
            ? "cursor-wait border-gray-300"
            : isDragOver
              ? "scale-105 border-primary bg-primary/5 cursor-pointer"
              : "border-gray-400 hover:border-primary hover:bg-primary/5 cursor-pointer"
        }`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={isLoading ? undefined : handleDrop}
      >
        <div className="flex flex-col items-center gap-4">
          {isLoading ? (
            <>
              <div className="h-16 w-16 animate-spin rounded-full border-4 border-primary border-t-transparent" />
              <div className="space-y-2 text-center">
                <Heading level={2} className="font-thin text-foreground">
                  Loading...
                </Heading>
                <Text className="text-muted-foreground text-sm">
                  Loading and initializing image processing
                </Text>
              </div>
            </>
          ) : (
            <>
              <CloudUpload
                className={`h-16 w-16 transition-colors ${
                  isDragOver ? "text-primary" : "text-muted-foreground group-hover:text-primary"
                }`}
              />

              <div className="space-y-2 text-center">
                <Heading level={2} className="font-thin text-foreground">
                  Upload an Image
                </Heading>
                <Text className="text-muted-foreground">
                  Drop an image here or click to select.
                  <br />
                </Text>
                <Text className="text-muted-foreground text-sm">
                  Accepted formats: JPEG, PNG and RAW formats including ARW (Sony), NEF (Nikon), DNG
                  and Apple ProRAW.
                </Text>
              </div>

              {error && (
                <div className="rounded border border-red-500/30 bg-red-500/10 p-3 text-center max-w-md mx-auto">
                  <Text className="text-red-900 dark:text-red-100 text-sm break-words">
                    Error: {error}
                  </Text>
                </div>
              )}

              <FileTrigger
                acceptedFileTypes={[
                  "image/jpeg",
                  "image/png",
                  "image/webp",
                  "image/bmp",
                  "image/tiff",
                  ".dng",
                  ".arw",
                  ".nef",
                  ".cr2",
                  ".raw",
                ]}
                allowsMultiple={false}
                onSelect={isLoading ? () => {} : handleFileSelect}
              >
                <Button
                  variant="outline"
                  className={`mt-4 transition-all ${
                    isDragOver
                      ? "border-primary bg-primary text-primary-foreground"
                      : "group-hover:border-primary group-hover:bg-primary group-hover:text-primary-foreground"
                  }`}
                  isDisabled={isLoading}
                >
                  <FileImage className="h-4 w-4" />
                  Select Image
                </Button>
              </FileTrigger>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
